import { Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { SharedMaterialModule } from "@shared/modules/shared-material.module";
import { SharedDsnModule } from "@shared/modules/shared-dsn.module";
import {
    OperatorType,
    PerformanceReferenceDTO
} from "@arch/_shared/types/source-reference.types";
import { DeviceService } from "@app/shared/services/device.service";
import { ColorsService } from "@arch/domain/application/services/colors.service";
import { ThemeService } from "@arch/domain/application/services/theme.service";
import { ThemesCustom } from "@app/views/auth/report/components/report-performance/report-performance.theme";

@Component({
    selector: "app-card-indicator-performance",
    imports: [SharedMaterialModule, SharedDsnModule],
    templateUrl: "./card-indicator-performance.component.html",
    styleUrl: "./card-indicator-performance.component.scss"
})
export class CardIndicatorPerformanceComponent {
    private readonly themeService = inject(ThemeService);

    @Input({ required: true }) type: "default" | "good" | "stable" | "bad" =
        "default";

    @Input({ required: false }) withTheme: boolean = false;
    @Input({ required: true }) title: string = "";
    @Input({ required: false }) titleHint?: string = "";
    @Input({ required: true }) month: string = "";
    @Input({ required: true }) monthValue: string = "";
    @Input({ required: false }) monthPercentageValue?: string = "";
    @Input({ required: true }) referenceLabel: string = "";
    @Input({ required: false }) referenceValue: string = "";
    @Input({ required: true }) variation: string = "";
    @Input({ required: false }) progressOptions: ProgressOptionsType;
    @Input({ required: false }) footerLabel?: string = "";
    @Input({ required: false }) operator?: OperatorType;
    @Input({ required: true }) direction?: "up" | "down" | "equal";
    @Input({ required: true }) polarity?: "positive" | "negative" | "neutral";
    @Input({ required: false }) info?: string = "";
    @Output() handleClickInfo = new EventEmitter<void>();

    private readonly deviceService = inject(DeviceService);
    public isMobile = this.deviceService.getIsMobile();
    public operators: Record<PerformanceReferenceDTO["operator"], string> = {
        "greater than": "fa-solid fa-greater-than",
        "less than": "fa-solid fa-less-than",
        equal: "fa-solid fa-equals",
        between: ""
    };

    public getColorThemeByType(): string {
        if (!this.withTheme) return "";

        switch (this.type) {
            case "good":
                return this.getThemeCustomColor("cyan-primary");
            case "stable":
                return this.getThemeCustomColor("yellow-dark");
            case "bad":
                return this.getThemeCustomColor("purple-dark");
            default:
                return "none";
        }
    }

    public isThemeCustom(): boolean {
        return this.themeService.getThemeName() !== "default";
    }

    private getThemeCustomColor(color: string) {
        const themename = this.themeService.getThemeName();
        return ThemesCustom[themename]?.[color] || "";
    }

    public getColorThemeByStatus(status: string) {
        if (!this.withTheme) return "";

        switch (status) {
            case "up":
                return this.getThemeCustomColor("cyan-primary");
            case "down":
                return this.getThemeCustomColor("purple-dark");
            case "equal":
                return this.getThemeCustomColor("yellow-medium");
            default:
                return "none";
        }
    }

    public statusIcons: Record<"up" | "down" | "equal", string> = {
        up: "fa-solid fa-arrow-up",
        down: "fa-solid fa-arrow-down",
        equal: "fa-solid fa-minus"
    };

    public onClickInfo() {
        this.handleClickInfo.emit();
    }
}

type ProgressOptionsType = {
    mark: number[];
    value: number;
    color?: "cyan" | "purple" | "yellow-medium";
    customColor?: string;
    range: {
        min: number;
        max: number;
    };
};
